import React, { FC } from 'react';
import { TypeProductPage } from '@/core/generated-types';
import { Link } from '@/navigation';
import { Button } from '../Buttons';
import { ShopifyProduct } from '@/lib/shopify';
import { YotpoReviewStars } from '../YotpoReviewStars';
import { TrustedShopReviews } from '@/components';
import { truncate } from '@/utils/helpers';
import {
  shouldUseYotpoReviews,
  shouldUseTrustedShopsReviews,
} from '@/utils/helpers';
import { PRODUCT_EVENT_TYPES, trackProductEvent } from '@/utils/rudderstack';
import { usePathname } from '@/navigation';

export interface CollectionProductProps {
  productPage: TypeProductPage;
  shopifyProduct?: ShopifyProduct;
  buttonText?: string;
}

const CollectionProduct: FC<CollectionProductProps> = ({
  productPage,
  shopifyProduct,
  buttonText,
}) => {
  const pathname = usePathname();
  const product = productPage.fields.product.fields;
  const listingImage = product.listingImage.fields.Image.fields.file.url;
  const featuredImg = product.featuredImage.fields.Image.fields.file.url;
  const productImg = listingImage || featuredImg;

  const onProductClick = () => {
    const payload = {
      url: pathname || '',
      productId: shopifyProduct?.id || '',
      sku: shopifyProduct?.variants[0].sku || '',
      category: shopifyProduct?.productType || '',
      name: product.title,
      ageRange: product.ageRange || '',
      ageUnits: '',
      price: shopifyProduct?.variants[0].price?.amount || 0,
      quantity: 1,
    };

    if (shopifyProduct) {
      trackProductEvent(PRODUCT_EVENT_TYPES.PRODUCT_CLICKED, payload);
    }
  };

  return (
    <Link
      href={`/products/${productPage.fields.slug}`}
      className="relative mx-auto mb-8 block flex w-full max-w-[328px] flex-col rounded-[5px] pb-10 shadow-lg hover:-translate-y-px hover:shadow-xl hover:transition md:max-w-[360px] "
      onClick={onProductClick}
    >
      <div
        className="mb-6 h-[16rem] flex-none rounded-t bg-[#e9f5f2] bg-contain bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${productImg})` }}
      ></div>
      <div className="flex flex-grow basis-auto flex-col justify-center px-8 text-left">
        <div
          className="mb-3 flex-grow text-[24px] font-bold"
          data-testid="product-name"
        >
          {product.title}
        </div>
        <div
          className="mb-4 text-left text-base md:block"
          data-testid="product-description"
        >
          {truncate(product.longDescription || '', 100)}
        </div>

        {/* ratings, price, and cta button */}
        <div>
          {shouldUseYotpoReviews() && (
            <div className="mb-4 flex flex-none">
              <YotpoReviewStars product={productPage.fields.product} />
            </div>
          )}
          {shouldUseTrustedShopsReviews() && (
            <div className="mb-4 flex flex-none">
              <TrustedShopReviews
                shopifyProduct={shopifyProduct}
                widgetType="ratings-short"
              />
            </div>
          )}
          <div
            className="mb-4 flex-none justify-end text-[20px] font-bold"
            data-testid="product-price"
          >
            {shopifyProduct?.variants[0].formattedPrice}
          </div>

          <div className="flex w-[178px] flex-auto flex-col justify-end">
            <Button
              ctaText={buttonText}
              classes="whitespace-nowrap text-base"
            />
          </div>
        </div>
      </div>
    </Link>
  );
};

export default React.memo(CollectionProduct);
